import { createSelector } from 'reselect';
import path from 'ramda/src/path';
import {
  createABAReducer,
  getABASelectors,
  RequestDataIds,
  State as ParamsState,
} from '@corefront/redux-aba';
import {
  commonConfigurationSelector,
  isAMSEnabledSelector,
} from './configuration';
import { formatAPIUrl } from '../../../utils/templater';
import {
  NATIVE_CHANNELS,
  DESKTOP_CHANNELS,
  MOBILE_CHANNELS,
} from '../../../constants/channels';
import type { RootState } from '../../store';

export const initialState: Partial<ParamsState> = {
  [RequestDataIds.BaseUrl]: '/',
  [RequestDataIds.AmsSegments]: [],
};

const reducer = createABAReducer(initialState);

export default reducer;

/**
 * SELECTORS
 */
export const rootSelector = (state: RootState) => state.app.params;

export const {
  containerSelector,
  languageSelector,
  baseURLSelector,
  brandSelector,
  channelSelector,
  proxyPrefixSelector,
  userTokenSelector,
  clientIdSelector,
  userDataSelector,
  depositUrlSelector,
  withdrawalUrlSelector,
  layoutDimensionsSelector,
  platformSelector,
  AMSSegmentsSelector: AMSSelector,
  crossDomainClickStreamIdSelector,
  brandHashSelector,
  applicationIdSelector,
} = getABASelectors(rootSelector);

export const AMSSegmentsSelector = createSelector(
  isAMSEnabledSelector,
  AMSSelector,
  (isAMSEnabled, segments) =>
    isAMSEnabled
      ? segments?.map?.((el) => String(el))
      : initialState[RequestDataIds.AmsSegments]
);

export const userNumberSelector = createSelector(userDataSelector, (userData) =>
  path(['number'], userData)
);
export const userCountryIdSelector = createSelector(
  userDataSelector,
  (userData) => path(['country', 'id'], userData)
);
export const userCountryNameSelector = createSelector(
  userDataSelector,
  (userData) => path(['country', 'name'], userData)
);
export const userCurrencyIdSelector = createSelector(
  userDataSelector,
  (userData) => path(['currency', 'id'], userData)
);
export const userCurrencyNameSelector = createSelector(
  userDataSelector,
  (userData) => path(['currency', 'name'], userData)
);

export const userBalanceSelector = createSelector(
  userDataSelector,
  (userData) => path(['balances', 'total'], userData)
);

export const isLoggedInSelector = createSelector(
  userTokenSelector,
  (userToken) => Boolean(userToken)
);
export const isDesktopSelector = createSelector(channelSelector, (channel) =>
  DESKTOP_CHANNELS.includes(channel)
);
export const isMobileSelector = createSelector(channelSelector, (channel) =>
  MOBILE_CHANNELS.includes(channel)
);
export const isNativeSelector = createSelector(channelSelector, (channel) =>
  NATIVE_CHANNELS.includes(channel)
);

export const frontApiPrefixSelector = createSelector(
  [commonConfigurationSelector, proxyPrefixSelector],
  (commonConfig, proxyPrefix) => {
    const frontAPIPrefix = commonConfig?.frontAPIPrefix ?? '';
    return formatAPIUrl(frontAPIPrefix, {
      ':proxyPrefix': proxyPrefix,
    });
  }
);
